import { useState, useMemo, useEffect } from "react";
import moment from "moment-hijri";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toHijri, hijriMonthRangeLabel } from "@/lib/hijri";
import { useLang } from "@/lib/i18n";

const VIEWS = ["day", "week", "month", "timeline", "agenda", "year"];

export const CalendarView = ({ events, hijriOffset = 0, onRangeChange, onEventClick }) => {
  const [view, setView] = useState(() => (typeof window !== "undefined" && window.innerWidth < 640 ? "agenda" : "month"));
  const [cursor, setCursor] = useState(() => moment());
  const { t, lang } = useLang();
  const locale = lang === "fr" ? "fr-FR" : "en-US";

  const range = useMemo(() => {
    if (view === "day") {
      return { start: cursor.clone().startOf("day"), end: cursor.clone().endOf("day") };
    }
    if (view === "week") {
      return { start: cursor.clone().startOf("isoWeek"), end: cursor.clone().endOf("isoWeek") };
    }
    if (view === "year") {
      return { start: cursor.clone().startOf("year"), end: cursor.clone().endOf("year") };
    }
    const s = cursor.clone().startOf("month").startOf("isoWeek");
    const e = cursor.clone().endOf("month").endOf("isoWeek");
    return { start: s, end: e };
  }, [cursor, view]);

  useEffect(() => {
    onRangeChange(range.start.format("YYYY-MM-DD"), range.end.format("YYYY-MM-DD"));
  }, [range, onRangeChange]);

  const eventsByDay = useMemo(() => {
    const map = {};
    (events || []).forEach((e) => {
      const key = e.start.slice(0, 10);
      (map[key] = map[key] || []).push(e);
    });
    return map;
  }, [events]);

  const navigateCursor = (dir) => {
    const unit = view === "week" ? "week" : view === "day" ? "day" : view === "year" ? "year" : "month";
    setCursor((c) => c.clone().add(dir, unit));
  };

  const gregLabel = view === "day"
    ? cursor.toDate().toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" })
    : view === "year"
    ? String(cursor.year())
    : view === "week"
    ? `${range.start.toDate().toLocaleDateString(locale, { day: "numeric", month: "short" })} – ${range.end.toDate().toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" })}`
    : cursor.toDate().toLocaleDateString(locale, { month: "long", year: "numeric" });
  const hijriLabel = hijriMonthRangeLabel(range.start.toDate(), range.end.toDate(), hijriOffset);

  const days = useMemo(() => {
    const list = [];
    const d = range.start.clone();
    while (d.isSameOrBefore(range.end, "day")) {
      list.push(d.clone());
      d.add(1, "day");
    }
    return list;
  }, [range]);

  const weekdays = useMemo(() => {
    const base = moment().startOf("isoWeek");
    return Array.from({ length: 7 }, (_, i) =>
      base.clone().add(i, "day").toDate().toLocaleDateString(locale, { weekday: "short" }));
  }, [locale]);

  const todayKey = moment().format("YYYY-MM-DD");

  return (
    <div data-testid="calendar-view">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h2 className="font-heading font-bold text-lg sm:text-xl capitalize">{gregLabel}</h2>
          <p className="text-sm font-mono-data text-muted-foreground">{hijriLabel} AH</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <div className="flex rounded-full border border-border overflow-x-auto no-scrollbar max-w-full">
            {VIEWS.map((v) => (
              <button
                key={v}
                data-testid={`calendar-view-${v}`}
                onClick={() => setView(v)}
                className={`px-3 sm:px-3.5 py-1.5 text-xs font-medium whitespace-nowrap transition-colors ${view === v ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
              >
                {t(v)}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" data-testid="calendar-prev" onClick={() => navigateCursor(-1)} className="h-8 w-8 rounded-full">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" data-testid="calendar-today" onClick={() => setCursor(moment())} className="h-8 rounded-full text-xs">
              {t("today")}
            </Button>
            <Button variant="outline" size="icon" data-testid="calendar-next" onClick={() => navigateCursor(1)} className="h-8 w-8 rounded-full">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {view === "agenda" || view === "day" ? (
        <div className="space-y-1" data-testid="calendar-agenda">
          {days.filter((d) => eventsByDay[d.format("YYYY-MM-DD")]).map((d) => {
            const key = d.format("YYYY-MM-DD");
            const hijri = toHijri(d.toDate(), hijriOffset);
            return (
              <div key={key} className="flex gap-3 sm:gap-4 py-3 border-b border-border">
                <div className="w-20 sm:w-28 shrink-0">
                  <p className={`font-heading font-bold text-sm ${key === todayKey ? "text-accent" : ""}`}>
                    {d.toDate().toLocaleDateString(locale, { weekday: "short", day: "numeric", month: "short" })}
                  </p>
                  <p className="text-[11px] font-mono-data text-muted-foreground">{hijri.day} {hijri.monthName}</p>
                </div>
                <div className="flex-1 min-w-0 space-y-1.5">
                  {eventsByDay[key].map((e) => (
                    <button key={e.id} data-testid="agenda-event" onClick={() => onEventClick(e)}
                      className="w-full min-w-0 text-left flex items-center gap-2 px-3 py-2 rounded-md border border-border hover:bg-muted transition-colors">
                      <span className="h-2.5 w-2.5 rounded-full shrink-0 ring-1 ring-foreground/25" style={{ background: e.display_color || e.mosque_color }} />
                      <span className="font-mono-data text-xs text-muted-foreground shrink-0">{e.start.slice(11, 16)}</span>
                      <span className="text-sm font-medium truncate min-w-0">{e.title}</span>
                      <span className="text-xs text-muted-foreground truncate hidden sm:inline">· {e.mosque_name}</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          {!days.some((d) => eventsByDay[d.format("YYYY-MM-DD")]) && (
            <p className="text-center text-muted-foreground py-16 text-sm">{t("no_events")}</p>
          )}
        </div>
      ) : view === "timeline" ? (
        <div className="relative pl-7" data-testid="calendar-timeline">
          <div className="absolute left-2.5 top-1 bottom-1 w-px bg-border" />
          {(events || []).map((e) => (
            <button key={e.id} data-testid="timeline-event" onClick={() => onEventClick(e)} className="relative block w-full text-left mb-5 group">
              <span className="absolute -left-[1.45rem] top-1.5 h-3.5 w-3.5 rounded-full border-2 border-background" style={{ background: e.display_color || e.mosque_color }} />
              <p className="text-xs font-mono-data text-muted-foreground capitalize">
                {new Date(e.start).toLocaleDateString(locale, { weekday: "short", day: "numeric", month: "short" })} · {e.start.slice(11, 16)} — {toHijri(e.start, hijriOffset).label}
              </p>
              <div className="mt-1.5 border border-border rounded-lg p-3.5 bg-card group-hover:bg-muted transition-colors">
                <p className="font-heading font-bold text-sm">{e.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{e.mosque_name}</p>
              </div>
            </button>
          ))}
          {(!events || events.length === 0) && (
            <p className="text-center text-muted-foreground py-16 text-sm">{t("no_events")}</p>
          )}
        </div>
      ) : view === "year" ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="calendar-year">
          {Array.from({ length: 12 }, (_, m) => {
            const first = cursor.clone().month(m).startOf("month");
            const offsetDays = first.isoWeekday() - 1;
            const dim = first.daysInMonth();
            return (
              <button
                key={m}
                data-testid="year-month-card"
                onClick={() => { setCursor(first); setView("month"); }}
                className="border border-border rounded-lg p-3 bg-card text-left hover:border-accent transition-colors"
              >
                <p className="font-heading font-bold text-sm capitalize mb-2">{first.toDate().toLocaleDateString(locale, { month: "long" })}</p>
                <div className="grid grid-cols-7 gap-y-1">
                  {Array.from({ length: offsetDays }).map((_, i) => <span key={`b${i}`} />)}
                  {Array.from({ length: dim }, (_, dd) => {
                    const key = first.clone().date(dd + 1).format("YYYY-MM-DD");
                    const has = !!eventsByDay[key];
                    const isToday = key === todayKey;
                    return (
                      <span key={dd} className={`text-[9px] font-mono-data h-4 w-4 flex items-center justify-center rounded-full mx-auto ${isToday ? "bg-accent text-accent-foreground font-bold" : has ? "bg-accent/20 text-accent font-bold" : "text-muted-foreground"}`}>
                        {dd + 1}
                      </span>
                    );
                  })}
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="grid grid-cols-7 border-b border-border bg-muted/50">
            {weekdays.map((w) => (
              <div key={w} className="px-2 py-2 text-[11px] font-mono-data uppercase tracking-wider text-muted-foreground text-center">{w}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {days.map((d, i) => {
              const key = d.format("YYYY-MM-DD");
              const hijri = toHijri(d.toDate(), hijriOffset);
              const inMonth = view === "week" || d.month() === cursor.month();
              const isToday = key === todayKey;
              const dayEvents = eventsByDay[key] || [];
              const isWeekend = d.isoWeekday() >= 6;
              return (
                <div key={key} data-testid="calendar-cell"
                  onClick={() => { if (window.innerWidth < 640 && dayEvents.length) { setCursor(d.clone()); setView("day"); } }}
                  className={`cal-cell p-1 sm:p-1.5 border-b border-r border-border ${(i + 1) % 7 === 0 ? "border-r-0" : ""} ${isWeekend ? "bg-muted/30" : ""} ${!inMonth ? "opacity-40" : ""}`}>
                  <div className="flex items-baseline justify-between px-0.5">
                    <span className={`font-heading font-bold text-xs sm:text-sm h-5 w-5 sm:h-6 sm:w-6 flex items-center justify-center rounded-full ${isToday ? "bg-accent text-accent-foreground" : ""}`}>
                      {d.date()}
                    </span>
                    <span className="text-[9px] sm:text-[10px] font-mono-data text-muted-foreground">{hijri.day}</span>
                  </div>
                  <div className="mt-1 space-y-0.5 hidden sm:block">
                    {dayEvents.slice(0, 3).map((e) => (
                      <button key={e.id} data-testid="calendar-event-chip" onClick={() => onEventClick(e)}
                        className="w-full text-left px-1.5 py-0.5 rounded text-[10px] sm:text-[11px] font-medium text-white truncate block hover:opacity-85 transition-opacity ring-1 ring-inset ring-foreground/20"
                        style={{ background: e.display_color || e.mosque_color }} title={e.title}>
                        <span className="hidden sm:inline font-mono-data">{e.start.slice(11, 16)} </span>{e.title}
                      </button>
                    ))}
                    {dayEvents.length > 3 && (
                      <p className="text-[10px] text-muted-foreground px-1">+{dayEvents.length - 3}</p>
                    )}
                  </div>
                  {dayEvents.length > 0 && (
                    <div className="flex sm:hidden flex-wrap gap-1 mt-1.5 px-0.5" data-testid="calendar-cell-dots">
                      {dayEvents.slice(0, 4).map((e) => (
                        <span key={e.id} className="h-1.5 w-1.5 rounded-full ring-1 ring-foreground/25" style={{ background: e.display_color || e.mosque_color }} />
                      ))}
                      {dayEvents.length > 4 && <span className="text-[8px] text-muted-foreground leading-none">+</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
